import React, { useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Link } from 'react-router-dom';

dayjs.extend(relativeTime);
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Grid,
  Button,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { StyledSnackbar } from '../../common/AppSnackbar';
import { ArrowBack, MoreVert } from '@mui/icons-material';
import RefreshIcon from '@mui/icons-material/Refresh';
import HistoryIcon from '@mui/icons-material/History';
import EventIcon from '@mui/icons-material/Event';
import DownloadIcon from '@mui/icons-material/Download';
import BoltIcon from '@mui/icons-material/Bolt';
import StopIcon from '@mui/icons-material/Stop';
import { CancelPresentation, Code, Fingerprint, List, AutoAwesome } from '@mui/icons-material';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { send_request } from '../../../utils/Request';
import ConfirmTerminateSessionDialog from '../ProjectLog/ConfirmTerminateSessionDialog';
import './TopGrid.css';

const ProgressViewPageHeader = ({
  title,
  onBack,
  workflow,
  sessionData,
  projectId,
  sessionId,
  getSessionData,
  setModalData,
  showTerminateButton,
  isTest,
  testId,
  isPolling,
  setIsPolling,
  isSplitView,
  navigate,
  ...props
}) => {
  const [terminateLoading, setTerminateLoading] = useState(false);
  const [openTerminateDialog, setOpenTerminateDialog] = useState(false);
  const [showCopySnackbar, setShowCopySnackBar] = useState(false);

  // Calculate the latest event date
  const calculateLastEvent = () => {
    let relativeDate = dayjs(sessionData.lastUpdated).fromNow();
    if (relativeDate === 'Invalid Date') return 'NaN ago';
    return relativeDate;
  };

  // Calculate AI credits count
  const calculateAICredits = () => {
    if (!sessionData.components || !Array.isArray(sessionData.components)) {
      return 0;
    }
    return sessionData.components.filter(
      component => component.type === 'ai_assistant' || component.type === 'AIAssistant'
    ).length;
  };

  const terminateSession = async projectSessionId => {
    if (!projectSessionId) return;
    setTerminateLoading(true);

    let url = `project-service/project/terminate/${projectSessionId}`;

    return send_request(url, '', { isTest: isTest }, 'post')
      .then(res => {
        getSessionData();
      })
      .then(() => {
        setTerminateLoading(false);
        setModalData({});
      })
      .catch(err => {
        console.log(err);
        setTerminateLoading(false);
      });
  };

  const downloadSessionDataJSON = () => {
    const jsonString = JSON.stringify(sessionData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `session_data_${sessionId}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const renderHeaderButtons = () => (
    <Box
      className="canvas-header-buttons"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        flexWrap: 'nowrap',
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <Grid container spacing={1} alignItems="center" wrap="nowrap">
        {!isSplitView && (
          <Grid item>
            <Button
              className="action-button"
              onClick={() => {
                const url = `/project/logs/${projectId}?test=${Boolean(
                  isTest
                )}&viewSession=${sessionId}`;
                navigate(url);
              }}
              startIcon={<List style={{ fontSize: '16px' }} />}
              size="small"
            >
              All Sessions
            </Button>
          </Grid>
        )}
        <Grid item>
          <Button
            className="action-button"
            onClick={() => setIsPolling(!isPolling)}
            startIcon={
              isPolling ? (
                <StopIcon style={{ fontSize: '16px' }} />
              ) : (
                <RefreshIcon style={{ fontSize: '16px' }} />
              )
            }
            size="small"
          >
            {isPolling ? 'Stop' : 'Refresh'}
          </Button>
        </Grid>
        <Grid item>
          <Button
            className="action-button"
            onClick={downloadSessionDataJSON}
            startIcon={<DownloadIcon style={{ fontSize: '16px' }} />}
            size="small"
          >
            JSON
          </Button>
        </Grid>
        {showTerminateButton && (
          <Grid item>
            <Button
              className={`terminate-button latest-run`}
              onClick={() => {
                setOpenTerminateDialog(true);
              }}
              disabled={terminateLoading}
              startIcon={<CancelPresentation />}
              size="small"
            >
              Terminate Session
              {terminateLoading && <CircularProgress className="loader" />}
            </Button>
          </Grid>
        )}

        <Grid item>
          <Tooltip title="View canvas version">
            <Button
              className="version-button"
              size="small"
              startIcon={<Code style={{ fontSize: '16px' }} />}
            >
              <Link
                to={`/project/view_canvas/${projectId}&version=${workflow.version}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Version {workflow.version}
              </Link>
            </Button>
          </Tooltip>
        </Grid>

        <Grid item>
          <CopyToClipboard
            text={sessionData.projectSessionId || ''}
            onCopy={() => setShowCopySnackBar(true)}
          >
            <Button className="session-button" size="small">
              <Fingerprint style={{ fontSize: '14px', marginRight: '4px', opacity: 0.7 }} />
              <span style={{ fontSize: '12px' }}>{sessionData.projectSessionId || 'No ID'}</span>
            </Button>
          </CopyToClipboard>
        </Grid>

        {(sessionData.componentCount !== undefined ||
          (sessionData.components && Array.isArray(sessionData.components))) && (
          <Grid item>
            <Tooltip title="Total actions executed" placement="top">
              <Button className="actions-chip" size="small">
                <BoltIcon />
                <span style={{ fontSize: '12px' }}>
                  {sessionData.componentCount !== undefined
                    ? sessionData.componentCount
                    : sessionData.components.length}
                </span>
              </Button>
            </Tooltip>
          </Grid>
        )}

        {sessionData.components &&
          Array.isArray(sessionData.components) &&
          calculateAICredits() > 0 && (
            <Grid item>
              <Tooltip title="AI credits used" placement="top">
                <Button className="ai-credits-chip" size="small">
                  <AutoAwesome />
                  <span style={{ fontSize: '12px' }}>{calculateAICredits()}</span>
                </Button>
              </Tooltip>
            </Grid>
          )}

        <Grid item>
          <span className="info-text">
            <EventIcon />
            Updated {calculateLastEvent()}
          </span>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <>
      <Paper
        elevation={0}
        className="fade-in canvas-page-header"
        sx={{
          height: '57px',
          minHeight: '57px',
          maxHeight: '57px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          borderRadius: 0,
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          width: isSplitView ? '100%' : 'calc(100vw - 48px)',
          left: isSplitView ? '0' : '48px',
          boxSizing: 'border-box',
          marginLeft: 0,
          marginRight: 0,
          marginTop: 0,
          pointerEvents: 'auto',
          overflow: 'hidden',
        }}
        {...props}
      >
        <Box
          className="staggered-animation"
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {/* Back Button and Title */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              mr: 1,
            }}
          >
            <IconButton
              size="small"
              onClick={onBack}
              sx={{
                mr: 0.5,
                p: 0.5,
              }}
            >
              <ArrowBack />
            </IconButton>

            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 500,
                flexShrink: 0,
                mr: 0.5,
                maxWidth: '200px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* Header Buttons */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              '& > *': {
                flexShrink: 0,
              },
            }}
          >
            {renderHeaderButtons()}
          </Box>
        </Box>
      </Paper>

      {openTerminateDialog && (
        <ConfirmTerminateSessionDialog
          isOpen={openTerminateDialog}
          close={() => setOpenTerminateDialog(false)}
          sessionId={sessionId}
          terminateSession={terminateSession}
        />
      )}

      <StyledSnackbar
        open={showCopySnackbar}
        message="Session ID copied"
        severity="success"
        onClose={() => setShowCopySnackBar(false)}
      />
    </>
  );
};

ProgressViewPageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  onBack: PropTypes.func.isRequired,
  workflow: PropTypes.object.isRequired,
  sessionData: PropTypes.object.isRequired,
  projectId: PropTypes.string.isRequired,
  sessionId: PropTypes.string.isRequired,
  getSessionData: PropTypes.func.isRequired,
  setModalData: PropTypes.func.isRequired,
  showTerminateButton: PropTypes.bool,
  isTest: PropTypes.bool,
  testId: PropTypes.string,
  isPolling: PropTypes.bool.isRequired,
  setIsPolling: PropTypes.func.isRequired,
  isSplitView: PropTypes.bool,
  navigate: PropTypes.func.isRequired,
};

export default ProgressViewPageHeader;
